import { z } from "zod";
import { idSchema, userSummarySchema } from "../common/primitives.js";
import { messageSchema } from "../chat/chat.schemas.js";
import { notificationSchema } from "../notifications/notification.schemas.js";

/** The API serves Socket.IO here; browsers send the session cookie with the handshake. */
export const REALTIME_PATH = "/api/socket.io";

/** Socket.IO event name every {@link ServerEvent} is delivered under. */
export const REALTIME_EVENT = "event";

/**
 * Events the server pushes. Each is addressed to specific users (e.g. a channel's members) when it
 * is published, so a client only receives what it is allowed to see. Clients react by updating or
 * invalidating their query caches.
 */
export const serverEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("MESSAGE_CREATED"),
    workspaceId: idSchema,
    channelId: idSchema,
    message: messageSchema,
  }),
  z.object({
    type: z.literal("MESSAGE_UPDATED"),
    workspaceId: idSchema,
    channelId: idSchema,
    message: messageSchema,
  }),
  z.object({
    type: z.literal("MESSAGE_DELETED"),
    workspaceId: idSchema,
    channelId: idSchema,
    messageId: idSchema,
  }),
  /** A channel the recipient belongs to was created, renamed, or its members changed. */
  z.object({ type: z.literal("CHANNEL_CHANGED"), workspaceId: idSchema, channelId: idSchema }),
  /** The recipient is no longer in the channel (left, removed, or the channel was deleted). */
  z.object({ type: z.literal("CHANNEL_REMOVED"), workspaceId: idSchema, channelId: idSchema }),
  /** Someone is typing in a channel the recipient belongs to. Ephemeral: never stored. */
  z.object({
    type: z.literal("USER_TYPING"),
    workspaceId: idSchema,
    channelId: idSchema,
    user: userSummarySchema,
  }),
  /** A new notification for the recipient. Clients refresh their tray and play the tone. */
  z.object({
    type: z.literal("NOTIFICATION_CREATED"),
    workspaceId: idSchema,
    notification: notificationSchema,
  }),
  /** The recipient's notifications changed elsewhere (read, dismissed): refresh the tray. */
  z.object({ type: z.literal("NOTIFICATIONS_CHANGED"), workspaceId: idSchema }),
  /**
   * A task changed (fields, assignees, tags, checklists, dependencies, watchers, or it was just
   * created). Id-only: refetch the task, its lists and its activity.
   */
  z.object({
    type: z.literal("TASK_CHANGED"),
    workspaceId: idSchema,
    projectId: idSchema,
    taskId: idSchema,
  }),
  /** A task was deleted: drop it from caches. */
  z.object({
    type: z.literal("TASK_DELETED"),
    workspaceId: idSchema,
    projectId: idSchema,
    taskId: idSchema,
  }),
  /** A comment on the task was added, edited or deleted: refetch its comments. */
  z.object({
    type: z.literal("COMMENT_CHANGED"),
    workspaceId: idSchema,
    projectId: idSchema,
    taskId: idSchema,
  }),
  /** The workspace's tag definitions changed (created, renamed, recolored, deleted). */
  z.object({ type: z.literal("TAGS_CHANGED"), workspaceId: idSchema }),
  /**
   * A member of `workspaceId` opened their first connection or closed their last one. One event is
   * sent per workspace the person shares with the recipient; clients ignore other workspaces.
   */
  z.object({
    type: z.literal("PRESENCE_CHANGED"),
    workspaceId: idSchema,
    userId: idSchema,
    online: z.boolean(),
  }),
]);
export type ServerEvent = z.infer<typeof serverEventSchema>;
export type ServerEventType = ServerEvent["type"];

/** What a client sends while its user types in a channel. */
export const typingPayloadSchema = z.object({ workspaceId: idSchema, channelId: idSchema });
export type TypingPayload = z.infer<typeof typingPayloadSchema>;

/** Socket.IO event map for server → client traffic. Heartbeats are built in. */
export interface ServerToClientEvents {
  event: (event: ServerEvent) => void;
}
/** Socket.IO event map for client → server traffic: only typing notices. */
export interface ClientToServerEvents {
  typing: (payload: TypingPayload) => void;
}
